import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, Save, RotateCcw, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_WELCOME_SUBJECT = "Welcome to {{partner_name}} - Your Will Planning Journey Begins!";
const DEFAULT_WELCOME_BODY = `Dear {{client_name}},

Welcome to {{partner_name}}! We're thrilled to have you join us on your journey to secure your family's future.

Creating a will is one of the most important steps you can take to protect your loved ones. With our platform, you can:

• Create a comprehensive, legally valid will
• Store important documents securely in your Life Vault
• Update your will anytime as life changes

Getting started is easy - simply log in to your account and follow our step-by-step guide.

If you have any questions, our team is here to help.

Warm regards,
{{partner_name}} Team

---
Powered by WillKart.ai`;

const PartnerEmailTemplates = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [template, setTemplate] = useState({
    welcome_subject: DEFAULT_WELCOME_SUBJECT,
    welcome_body: DEFAULT_WELCOME_BODY
  });
  const [partnerName, setPartnerName] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('partnerToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const name = localStorage.getItem('partnerName') || 'Your Company';
    setPartnerName(name);
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/partner/email-templates`, { headers });
      if (response.data) {
        setTemplate({
          welcome_subject: response.data.welcome_subject || DEFAULT_WELCOME_SUBJECT,
          welcome_body: response.data.welcome_body || DEFAULT_WELCOME_BODY
        });
      }
    } catch (error) {
      console.error('Error fetching template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/partner/email-templates`, template, { headers });
      toast.success('Email template saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setTemplate({
      welcome_subject: DEFAULT_WELCOME_SUBJECT,
      welcome_body: DEFAULT_WELCOME_BODY
    });
    toast.info('Template reset to default');
  };

  const renderPreview = (text) => {
    return text
      .replace(/\{\{client_name\}\}/g, 'John Doe')
      .replace(/\{\{partner_name\}\}/g, partnerName)
      .replace(/\{\{client_email\}\}/g, 'john.doe@example.com');
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400 mx-auto"></div>
          <p className="mt-4 text-slate-400">Loading template...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="email-templates-page">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Email Templates</h1>
        <p className="text-slate-400 mt-1">Customize the welcome email sent to new clients</p>
      </div>

      {/* Welcome Email Template */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="font-bold text-lg text-white">Welcome Email</h2>
              <p className="text-sm text-slate-400">Sent when a new client registers</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
        </div>

        {/* Available Variables */}
        <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
          <p className="text-sm text-slate-400 mb-2">Available variables (will be replaced automatically):</p>
          <div className="flex flex-wrap gap-2">
            <code className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded">{'{{client_name}}'}</code>
            <code className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded">{'{{client_email}}'}</code>
            <code className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded">{'{{partner_name}}'}</code>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Editor */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="subject" className="text-slate-300 mb-2 block">Email Subject</Label>
              <Input
                id="subject"
                value={template.welcome_subject}
                onChange={(e) => setTemplate(t => ({ ...t, welcome_subject: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white"
                placeholder="Enter email subject..."
                data-testid="email-subject-input"
              />
            </div>
            <div>
              <Label htmlFor="body" className="text-slate-300 mb-2 block">Email Body</Label>
              <Textarea
                id="body"
                value={template.welcome_body}
                onChange={(e) => setTemplate(t => ({ ...t, welcome_body: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white min-h-[400px] font-mono text-sm"
                placeholder="Enter email body..."
                data-testid="email-body-input"
              />
            </div>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className="bg-white rounded-lg p-6 text-slate-900">
              <div className="border-b border-slate-200 pb-4 mb-4">
                <p className="text-xs text-slate-500 mb-1">From: help@willkart.in</p>
                <p className="text-xs text-slate-500 mb-1">To: john.doe@example.com</p>
                <p className="text-xs text-slate-500 mb-2">CC: {localStorage.getItem('partnerEmail') || 'partner@example.com'}</p>
                <p className="font-semibold text-lg">{renderPreview(template.welcome_subject)}</p>
              </div>
              <div className="whitespace-pre-wrap text-sm leading-relaxed">
                {renderPreview(template.welcome_body)}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-slate-800">
          <Button
            variant="outline"
            onClick={handleReset}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="reset-template-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Default
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
            data-testid="save-template-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <p className="text-sm text-blue-300">
          <strong>Note:</strong> This welcome email is automatically sent to new clients when they register 
          through your client portal. The partner email ({localStorage.getItem('partnerEmail') || 'your email'}) 
          will be CC'd on all welcome emails.
        </p>
      </div>
    </div>
  );
};

export default PartnerEmailTemplates;
